import { MeetingOrderedSession } from "@kalyzee/kast-web-sdk";
import { ChatRef, CyclingQRCodeData, QRCodeData, QRCodeHooks, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { ChatroomStatus, Meeting, MeetingRoom, MeetingSession, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { PageMap, PageSlaveMode } from "../../components/navigation/PageContext";
import { useMessagingBridgeManagerMasterContext, useMessagingBridgeManagerSlaveContext } from "../../contexts/messageBridgeManager";
import { MessagingBridgeManagerMasterClient } from "../../helpers/bridge/master";
import { useFocusedMeetingAPI, useMeetingSelector } from "../meeting";
import { sdkClient } from "../../helpers/sdk";
import { getHttpApiBaseUrl } from "../../helpers/request";
import { SessionMode } from "../../contexts/appStore/hooks";
import { useVideoCarousel } from "./videoCarousel";
import { useThemeContext } from "@kalyzee/kalyzee-ui";

export const DEFAULT_CAROUSEL_INTERVAL = 60000;
export const DEFAULT_CAROUSEL_DELAY = 5000;

export interface SlaveQRCodeData {
  display: boolean,
  data?: QRCodeData,
}

interface CarouseInfo {
  sessions: MeetingOrderedSession[],
  displayNextCarousel?: Date,
  displayNextCarouselTimeout?: NodeJS.Timer,
  /*
    Here screens designates unique video groups,
    so it will have to be renamed videoGroups

    And then, the screen will be served the current round of the
    video group they specified

    Each screen, depending on the video area it has or the number of it
    will have a max capacity, so there's that to take into account
  */
  videoGroups: {
    index: number, 
    // Session Mode
    capacity: number, // has to be the smallest of all screens using this videoGroup
    roundIndex: number, 
    nextRoundIndex?: number, 
    rounds: { enabled: boolean, items: MeetingOrderedSession[] }[],
  }[],
}


export const useMeetingMasterMessagingBridge = () => {
  const bridge = useMessagingBridgeManagerMasterContext();
  const bridgeRef = useRefAutoUpdate(bridge);

  const meetingCtx = useFocusedMeetingAPI();
  const [meeting, meetingRef] = useMeetingSelector(meetingCtx, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(meetingCtx, (state) => state.session);
  const [chatAdapter, chatAdapterRef] = useMeetingSelector(meetingCtx, (state) => {
    const mainChatroom = state.meeting?.chatrooms?.[0];
    if (!mainChatroom) return undefined;
    return state.chatAdapters[mainChatroom.id];
  });

  // TODO : extract from settings

  const hasQRCodeClientsFn = () => {
    const clients = bridgeRef.current?.getActiveClients<SessionMode.SLAVE, PageMap[SessionMode.SLAVE]>(true);
    //return clients && clients.some(c => c.data?.data.screen === 1);
    return false; // TODO
  }
  const [hasQRCodeClients, setHasQRCodeClients] = useState(() => hasQRCodeClientsFn());
  useEffect(() => {
    const handle = bridgeRef.current?.addEventListener("clientChange", () => {
      setHasQRCodeClients(hasQRCodeClientsFn());
    });
    return () => handle?.flush();
  }, []);

  const getQRCodeData = useCallback(async (): Promise<QRCodeData | undefined> => {
    const wsClient = sdkClient.getWebsocketClient();
    if (!wsClient) return undefined;
    const meetingId = meeting?.id;
    if (!meetingId) return undefined;
    const result = await wsClient.emitter.meeting.generateUserDataOnsiteTicket({ meetingId });
    if (!result.response || result.error) return undefined;
    const { ticket, ticketExpiresAfter, ticketExpiresAt } = result.response;
    const url = `${getHttpApiBaseUrl()}/meeting/ticket/${ticket}`;
    return {
      value: url,
      expiration: ticketExpiresAfter && ticketExpiresAt ? {
        after: ticketExpiresAfter,
        at: new Date(ticketExpiresAt),
      } : undefined,
    }
  }, [meeting?.id]);
  const qrCodeData = QRCodeHooks.useCycling(
    meeting?.callEnabled && !meeting.callWasDone && hasQRCodeClients ? getQRCodeData : undefined,
    5000,
  );

  const themeCtx = useThemeContext();
  
  const getMessagingBridgeClients = (): MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[] => {
    return bridgeRef.current?.getActiveClients(true).filter((c) => c.data?.mode === SessionMode.SLAVE && c.data?.page === PageSlaveMode.MEETING) ?? [];
  };

  const broadcastMeeting = useCallback(
    debounce(
      () => {
        if (!bridge) return;
        const meeting = meetingRef.current;
        const session = sessionRef.current;
        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge.postMeeting(meeting, session);
        } else {
          bridge.postMeeting(undefined, undefined);
        }
      },
      100,
      { maxWait: 300 }
    ),
    [bridge]
  );

  const broadcastChat = useCallback(
    debounce(
      () => {
        if (!bridge) return;
        const adapter = chatAdapterRef.current;
        if (!adapter) return;
        const chatroom = meetingRef.current?.chatrooms?.find((c) => c.id === adapter.getChatroomId());
        if (!chatroom) return;
        bridge.postChat(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
      },
      100,
      { maxWait: 300 }
    ),
    [bridge]
  );

  const CarouselAPI = useVideoCarousel({
    meeting,
    session,
    getClients: getMessagingBridgeClients,
    onCarousel: (data, delay, page, pageCount, id) => {
      bridgeRef.current?.postDisplayCarousel(data, delay, page, pageCount, id);
    },
  });

  useEffect(() => {
    if (!bridge) return;
    const getMeetingListener = bridge.addEventListener("requestMeeting", (_, respond) => {
      respond(meetingRef.current, sessionRef.current);
    });
    const getChatListener = bridge.addEventListener("requestChat", (_, respond) => {
      const adapter = chatAdapterRef.current;
      if (!adapter) return;
      const chatroom = meetingRef.current?.chatrooms?.find((c) => c.id === adapter.getChatroomId());
      if (!chatroom) return;
      respond(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
    });

    const clientDataListener = bridge.addEventListener("clientChange", () => {
      const newClients = getMessagingBridgeClients();
      if (newClients.length) {
        if (!CarouselAPI.isRunning()) CarouselAPI.start();
        else CarouselAPI.update();
      } else if (!newClients.length) {
        CarouselAPI.stop();
      }
    });

    return () => {
      getMeetingListener.flush();
      getChatListener.flush();
      clientDataListener.flush();
    };
  }, [bridge, CarouselAPI]);

  useEffect(() => {
    broadcastMeeting();
  }, [meeting, session])

  useEffect(() => {
    if (!meetingCtx) {
      CarouselAPI.stop();
      return;
    }
    if (!meeting) {
      CarouselAPI.stop();
    } else {
      const clients = getMessagingBridgeClients();
      if (!CarouselAPI.isRunning() && clients.length) {
        CarouselAPI.start();
      } else if (CarouselAPI.isRunning()) {
        CarouselAPI.update();
      }
    }
    broadcastMeeting();
  }, [meetingCtx, broadcastMeeting, CarouselAPI]);

  useEffect(() => {
    const chatCleanMessagesListener = chatAdapter?.addEventListener("cleanMessages", () => {
      broadcastChat();
    });
    const chatNewMessagesListener = chatAdapter?.addEventListener("newMessages", () => broadcastChat());
    const chatUpdateMessageListener = chatAdapter?.addEventListener("updateMessage", () => broadcastChat());
    const chatUpdateManyMessagesListener = chatAdapter?.addEventListener("updateMessages", () => broadcastChat());
    const chatStateListener = chatAdapter?.addEventListener("state", () => broadcastChat());
    return () => {
      chatCleanMessagesListener?.flush();
      chatNewMessagesListener?.flush();
      chatUpdateMessageListener?.flush();
      chatUpdateManyMessagesListener?.flush();
      chatStateListener?.flush();
    };
  }, [chatAdapter, broadcastChat]);

  useEffect(() => {
    if (!bridge) return;
    bridge.postQRCodeData(qrCodeData);
  }, [bridge, qrCodeData]);

  useEffect(() => {
    if (!bridge) return;
    bridge.postTheme(themeCtx.preferredTheme);
  }, [bridge, themeCtx]);

  useEffect(() => {
    return () => {
      bridgeRef.current?.postMeeting(undefined, undefined);
      CarouselAPI.stop();
    };
  }, []);

  return bridge;
};

export const useMeetingSlaveMessagingBridge = (chatRef?: React.RefObject<ChatRef>) => {
  const bridge = useMessagingBridgeManagerSlaveContext();
  const bridgeRef = useRefAutoUpdate(bridge);
  const [meeting, setMeeting] = useState<Meeting>();
  const [rooms, setRooms] = useState<MeetingRoom[]>();
  const [session, setSession] = useState<MeetingSession>();
  const [qrCodeData, setQRCodeData] = useState<CyclingQRCodeData>();
  const isInitiatedRef = useRef(false);
  const currentChatroomId = useRef<string>();
  const themeCtx = useThemeContext();

  useEffect(() => {
    if (!bridge) return;
    const readyListener = bridge.addEventListener("ready", () => {
      bridge.postRequestMeeting();
    });
    const meetingListener = bridge.addEventListener("meeting", (msg) => {
      const { meeting, session } = msg.content;
      setMeeting(meeting);
      setRooms(rooms);
      setSession(session);
    });
    const chatListener = bridge.addEventListener("chat", (msg) => {
      const { state, messages } = msg.content;
      if (state === ChatroomStatus.disabled) chatRef?.current?.disable();
      else chatRef?.current?.enable();
      chatRef?.current?.setMessages(
        messages.map((m) => {
          m.date = new Date(m.date); // we need to convert date to Date (date change to "string" type by passing by the websocket)
          return m;
        })
      );
    });

    const qrCodeListener = bridge.addEventListener("qrCodeData", (msg) => {
      const { data } = msg.content;
      setQRCodeData(data);
    });

    const themeListener = bridge.addEventListener("theme", (msg) => {
      const theme = msg.content;
      themeCtx.setPreferredTheme(theme);
    });

    const disconnectListener = bridge.addEventListener("disconnect", () => {
      setMeeting(undefined);
      setRooms(undefined);
      setSession(undefined);
      setQRCodeData(undefined);
      currentChatroomId.current = undefined;
      bridge.postRequestMeeting();
    });

    if (!isInitiatedRef.current) {
      isInitiatedRef.current = true;
      bridge.postRequestMeeting();
    }

    return () => {
      readyListener.flush();
      meetingListener.flush();
      chatListener?.flush();
      disconnectListener.flush();
      qrCodeListener.flush();
      themeListener.flush();
    };
  }, [bridge, themeCtx]);

  useEffect(() => {
    if (!meeting) return;
    if (meeting.chatrooms?.length) {
      const chatroomId = meeting.chatrooms[0].id;
      if (chatroomId !== currentChatroomId.current) {
        currentChatroomId.current = chatroomId;
        bridgeRef.current?.postRequestChat(chatroomId);
      }
    }
  }, [meeting]);

  return { bridge, meeting, rooms, session, qrCodeData };
};
